import { useMemo } from 'react';
import { useNotifications } from '@/providers/notificationHooks';
import type { UiTone } from '@/theme/uiTokens';

export const toastMessages = {
  'clipboard.copied': ({ label }: { label: string }) => `${label} copied to clipboard`,
  'clipboard.copyFailed': 'Failed to copy',
  'clipboard.unavailable': 'Clipboard not available',
  'entity.created': ({ entity, name }: { entity: string; name?: string | null }) =>
    name ? `Created ${entity} ${name}` : `${sentenceCase(entity)} created`,
  'entity.updated': ({ entity, name }: { entity: string; name?: string | null }) =>
    name ? `Updated ${entity} ${name}` : `${sentenceCase(entity)} updated`,
  'entity.deleted': ({ entity, name }: { entity: string; name?: string | null }) =>
    name ? `Deleted ${entity} ${name}` : `${sentenceCase(entity)} deleted`,
  'entity.restored': ({ entity, name }: { entity: string; name?: string | null }) =>
    name ? `Restored ${entity} ${name}` : `${sentenceCase(entity)} restored`,
  'entity.linked': ({ entity, target }: { entity: string; target: string }) =>
    `Linked ${entity} to ${target}`,
  'entity.unlinked': ({ entity, target }: { entity: string; target: string }) =>
    `Unlinked ${entity} from ${target}`,
  'entity.submitted': ({ entity }: { entity: string }) => `${sentenceCase(entity)} submitted`,
  'entity.revoked': ({ entity, name }: { entity: string; name?: string | null }) =>
    name ? `Revoked ${entity} ${name}` : `${sentenceCase(entity)} revoked`,
  'admin.signupEmailAllowed': ({ email }: { email: string }) => `Allowed ${email} to sign up`,
  'admin.externalCleanupRetried': ({ name }: { name: string }) =>
    `Retried external cleanup for ${name}`,
  'admin.userSessionsRevoked': ({ email }: { email: string }) => `Forced sign-out for ${email}`,
  'project.dataWipeScheduled': ({ name }: { name: string }) => `Scheduled data wipe for ${name}`,
  'project.dataWiped': ({ name }: { name: string }) => `Wiped data for ${name}`,
  'project.moduleDataWipeScheduled': ({
    moduleLabel,
    projectName,
  }: {
    moduleLabel: string;
    projectName: string;
  }) => `Scheduled ${moduleLabel} wipe for ${projectName}`,
  'project.moduleDataWiped': ({
    moduleLabel,
    projectName,
  }: {
    moduleLabel: string;
    projectName: string;
  }) => `Wiped ${moduleLabel} for ${projectName}`,
  'release.markedReleased': ({ name }: { name: string }) => `Marked release ${name} as released`,
  'invite.sent': ({ email }: { email: string }) => `Invitation sent to ${email}`,
  'invite.cancelled': ({ email }: { email: string }) => `Cancelled invite for ${email}`,
  'upload.filesUploading': ({ count, fileLabel }: { count: number; fileLabel: string }) =>
    `Uploading ${String(count)} ${fileLabel}`,
  'upload.snippetCopied': ({ format }: { format: string }) => `Copied ${format} upload snippet`,
  'uploadToken.created': 'Created an upload token. Copy it now; it will not be shown again.',
  'uploadToken.testPassed': 'Upload token test passed',
  'project.testConfigurationUpdated': 'Updated test configuration',
  'project.headerMappingUpdated': 'Updated header mapping',
  'project.statusMappingUpdated': 'Updated status mapping',
  'context.workspaceSwitched': ({ name }: { name: string }) => `Switched to ${name}`,
  'context.viewingAllProjects': 'Viewing all projects',
  'context.projectSwitched': ({ name }: { name: string }) => `Switched to ${name}`,
  'context.projectViewOnly': ({ name }: { name: string }) =>
    `${name} is view-only on the current plan`,
  'webhook.keyValid': 'Webhook key is valid and ready to use',
  'tags.updated': 'Tags updated',
  'plan.checkoutCompleted': 'Checkout completed',
  'plan.checkoutCancelled': 'Checkout cancelled',
  'plan.changed': ({ planName }: { planName: string }) => `Plan changed to ${planName}`,
  'plan.changeScheduled': ({ planName }: { planName: string }) =>
    `Plan change to ${planName} scheduled for the next billing date`,
  'plan.renewalCancelled':
    'Renewal cancelled. Your current plan will remain active until the billing date.',
  'plan.scheduledDowngradeCancelled': ({ planName }: { planName: string }) =>
    `Scheduled downgrade cancelled. ${planName} will remain active.`,
  'project.limitReached': 'Project limit reached',
  'workspace.accessLost': 'You no longer have access to that workspace',
} as const;

export type ToastMessageCatalog = typeof toastMessages;
export type ToastMessageKey = keyof ToastMessageCatalog;

export type ToastMessageArgs<K extends ToastMessageKey> = ToastMessageCatalog[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

type ToastOptions<K extends ToastMessageKey> = {
  args?: ToastMessageArgs<K>;
  timeoutMs?: number;
  tone?: UiTone;
};

type NotifyToast = <K extends ToastMessageKey>(key: K, options?: ToastOptions<K>) => void;

const sentenceCase = (value: string): string =>
  value.length === 0 ? value : `${value[0].toUpperCase()}${value.slice(1)}`;

export const getToastMessage = <K extends ToastMessageKey>(
  key: K,
  args?: ToastMessageArgs<K>,
): string => {
  const entry = toastMessages[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: ToastMessageArgs<K>) => string)(
      args ?? ({} as ToastMessageArgs<K>),
    );
  }
  return entry;
};

const createNotifyToast = (notify: ReturnType<typeof useNotifications>['notify']): NotifyToast => {
  return (key, options) => {
    notify({
      message: getToastMessage(key, options?.args),
      tone: options?.tone,
      timeoutMs: options?.timeoutMs,
    });
  };
};

export const useToastNotifications = (): {
  notifyToast: NotifyToast;
  notifyToastSuccess: <K extends ToastMessageKey>(
    key: K,
    args?: ToastMessageArgs<K>,
    options?: Omit<ToastOptions<K>, 'args' | 'tone'>,
  ) => void;
  notifyToastInfo: <K extends ToastMessageKey>(
    key: K,
    args?: ToastMessageArgs<K>,
    options?: Omit<ToastOptions<K>, 'args' | 'tone'>,
  ) => void;
  notifyToastWarning: <K extends ToastMessageKey>(
    key: K,
    args?: ToastMessageArgs<K>,
    options?: Omit<ToastOptions<K>, 'args' | 'tone'>,
  ) => void;
  notifyToastError: <K extends ToastMessageKey>(
    key: K,
    args?: ToastMessageArgs<K>,
    options?: Omit<ToastOptions<K>, 'args' | 'tone'>,
  ) => void;
} => {
  const { notify } = useNotifications();

  return useMemo(() => {
    const notifyToast = createNotifyToast(notify);
    return {
      notifyToast,
      notifyToastSuccess: (key, args, options) => {
        notifyToast(key, { ...options, args, tone: 'green' });
      },
      notifyToastInfo: (key, args, options) => {
        notifyToast(key, { ...options, args, tone: 'blue' });
      },
      notifyToastWarning: (key, args, options) => {
        notifyToast(key, { ...options, args, tone: 'orange' });
      },
      notifyToastError: (key, args, options) => {
        notifyToast(key, { ...options, args, tone: 'red' });
      },
    };
  }, [notify]);
};

type ToastSampleArgs = {
  [K in ToastMessageKey]?: ToastMessageArgs<K>;
};

const sampleArgs: ToastSampleArgs = {
  'clipboard.copied': { label: 'Project ID' },
  'entity.created': { entity: 'project', name: 'Checkout' },
  'entity.updated': { entity: 'project', name: 'Checkout' },
  'entity.deleted': { entity: 'project', name: 'Checkout' },
  'entity.restored': { entity: 'upload', name: 'upload-1' },
  'entity.linked': { entity: 'test', target: 'release' },
  'entity.unlinked': { entity: 'test', target: 'release' },
  'entity.submitted': { entity: 'feedback' },
  'entity.revoked': { entity: 'token', name: 'Deploy token' },
  'admin.signupEmailAllowed': { email: 'teammate@example.com' },
  'admin.externalCleanupRetried': { name: 'Acme' },
  'admin.userSessionsRevoked': { email: 'teammate@example.com' },
  'project.dataWipeScheduled': { name: 'Checkout' },
  'project.dataWiped': { name: 'Checkout' },
  'project.moduleDataWipeScheduled': { moduleLabel: 'test data', projectName: 'Checkout' },
  'project.moduleDataWiped': { moduleLabel: 'test data', projectName: 'Checkout' },
  'release.markedReleased': { name: 'v1.2' },
  'invite.sent': { email: 'teammate@example.com' },
  'invite.cancelled': { email: 'teammate@example.com' },
  'upload.filesUploading': { count: 2, fileLabel: 'files' },
  'upload.snippetCopied': { format: 'CSV' },
  'context.workspaceSwitched': { name: 'Acme' },
  'context.projectSwitched': { name: 'Checkout' },
  'context.projectViewOnly': { name: 'Archive' },
  'plan.changed': { planName: 'Team' },
  'plan.changeScheduled': { planName: 'Solo' },
  'plan.scheduledDowngradeCancelled': { planName: 'Team' },
};

const getToastMessageSample = <K extends ToastMessageKey>(
  key: K,
  args: ToastMessageArgs<K> | undefined,
): { key: ToastMessageKey; value: string } => ({
  key,
  value: getToastMessage(key, args),
});

export const getToastMessageSamples = (): Array<{ key: ToastMessageKey; value: string }> =>
  (Object.keys(toastMessages) as ToastMessageKey[]).map((key) => ({
    key,
    value: getToastMessageSample(key, sampleArgs[key] as never).value,
  }));
